import { Controller } from 'stimulus';
import events from 'lib/events';
import {
  triggerCustomEvent,
  getWindowWidth,
  splitTestEnabled,
} from 'lib/helpers';
import SearchStateManager from 'lib/search-state';
import MediaMatcher from 'lib/media-matcher';
import { initTooltips } from 'ez-styles/assets/js/utils/tooltips';
import Cookies from 'js-cookie';

const mobileWidth = 750;
const searchState = new SearchStateManager();
const mediaMatcher = new MediaMatcher();

export default class SearchFiltersController extends Controller {
  static targets = ['filterOption'];

  filterDefaults = [
    'sort-best',
    'license-all',
    'resolution-hd',
  ];

  connect() {
    window.addEventListener('search:toggle_search_filters', this.toggleSearchFilters);
    window.addEventListener('search:reset_hidden_filters', this.resetHiddenFilters);

    this.contentType = document.querySelector('[data-search-form-target="input"]').getAttribute('data-content-type');

    this.setupSidebarClassMediaQueries();

    this.setTogglesFromState();
    initTooltips(); // Need to make sure tooltips work every time filters are loaded

    mediaMatcher.evaluateAll();
    mediaMatcher.addListeners();
  }

  disconnect() {
    mediaMatcher.removeListeners();
    window.removeEventListener('search:toggle_search_filters', this.toggleSearchFilters);
    window.removeEventListener('search:reset_hidden_filters', this.resetHiddenFilters);

    window.removeEventListener(events.search.remove_filter, this.removeFilter);

    if (splitTestEnabled('remove-mobile-filters-apply')) {
      this.setSearchFiltersHiddenKey('mobile-filter-panel', false);
    }
  }

  setupSidebarClassMediaQueries() {
    mediaMatcher.addMediaQuery(`(max-width: ${mobileWidth}px)`,
      false,
      () => {
        const hasActiveSidebar = document.querySelector('body.has-active-mobile-sidebar');
        if (hasActiveSidebar) document.body.classList.remove('has-active-mobile-sidebar');
      });
  }

  setTogglesFromState = () => {
    this.finishedLoading = false;

    const activeTogglesFromState = searchState.getRawParamKeys();

    const allFilterInputs = [
      ...this.element.querySelectorAll('.ez-input--checkbox input'),
    ];

    allFilterInputs.forEach((checkbox) => {
      const checkboxFilterParam = checkbox.closest('.ez-input--checkbox').getAttribute('data-filter-param');

      if (activeTogglesFromState.includes(checkboxFilterParam)) {
        checkbox.checked = true; // eslint-disable-line no-param-reassign
      } else if (!searchState.omittedFromState(checkbox.name)) {
        checkbox.checked = false; // eslint-disable-line no-param-reassign
      }
    });

    this.finishedLoading = true;
  };

  removeFilter = (event) => {
    searchState.removeParamKey(event.detail.filterKey);

    this.applyFilters();
  }

  setFilter = (event = false, inputEl = false) => {
    const filterInput = event ? event.currentTarget : inputEl;
    const thisWrap = filterInput.closest('.ez-input');
    const checkbox = filterInput;
    let param;
    let filterValue;

    if (filterInput.type === 'range') {
      param = `${thisWrap.getAttribute('data-filter-param')}-${this.getFilterRangeValue(thisWrap)}`;
      filterValue = this.getFilterRangeValue(thisWrap);
    } else {
      param = thisWrap.getAttribute('data-filter-param');
      filterValue = (checkbox.checked ? 1 : 0);
    }

    triggerCustomEvent(events.search.filter, {
      filter: param,
      filterValue,
      searchTerm: this.searchTerm,
    });

    if (searchState.omittedFromState(checkbox.name)) {
      this.contentType = checkbox.value;
    } else if (checkbox.checked === true || filterInput.type === 'range') {
      searchState.updateParamKey(param);
    } else {
      searchState.removeParamKey(param);
    }

    if (checkbox.getAttribute('type') === 'radio' && checkbox.checked === false) {
      return;
    }

    if (splitTestEnabled('remove-mobile-filters-apply')) {
      this.applyFilters();
    } else if (getWindowWidth() >= 750) this.applyFilters();
  };

  seoFilterClick = (event) => {
    const ezInput = event.target.closest('.ez-input');
    const radio = ezInput.querySelector('input[type=radio]');

    if (getWindowWidth() < 750) {
      event.preventDefault();
      this.setFilter(false, radio);
    } else {
      // seo filters are on content type filters, need to reset filter state on click
      searchState.resetStateToDefault();
    }

    // if the user clicks an seo link, let's update the radio button visually before the page loads
    const filterSection = event.target.closest('.ez-search-filters__sidebar-section');
    const selected = filterSection.querySelectorAll('.is-selected');
    selected.forEach((el) => {
      el.classList.remove('is-selected');
    });

    radio.checked = true;
    radio.classList.add('is-selected');

    // if the user clicked the fake checkbox, trigger link click.
    if (!event.target.href) event.target.querySelector('a').click();
  }

  getFilterRangeValue = (wrapper) => {
    const rangeAValue = parseFloat(wrapper.querySelector('[data-target="duration-range.rangeA"]').value);
    const rangeBValue = parseFloat(wrapper.querySelector('[data-target="duration-range.rangeB"]').value);

    const minValue = rangeAValue <= rangeBValue ? rangeAValue : rangeBValue;
    const maxValue = rangeAValue > rangeBValue ? rangeAValue : rangeBValue;

    return `${minValue}_${maxValue}`;
  }

  changeFilter(event) {
    const thisWrap = event.target.closest('.ez-input');
    thisWrap.querySelector('input').click();
  }

  applyFilters() {
    if (getWindowWidth() < 750 && splitTestEnabled('remove-mobile-filters-apply')) {
      this.setSearchFiltersHiddenKey('mobile-filter-panel', true);
    }

    triggerCustomEvent(events.search.apply_filters, {
      contentType: this.contentType,
    });

    if (getWindowWidth() < 750) {
      if (!splitTestEnabled('remove-mobile-filters-apply')) {
        this.hideSearchFilters();
      }
    }
  }

  toggleSearchFilters = () => {
    const windowWidth = getWindowWidth();
    this.sendShowHideEvent(windowWidth);

    if (windowWidth > 750) {
      this.setSearchFiltersHiddenKey('filter-panel', document.body.classList.contains('has-active-sidebar'));
      document.body.classList.toggle('has-active-sidebar');
      triggerCustomEvent('InitAllGrids');
    } else {
      document.body.classList.toggle('has-active-mobile-sidebar');

      if (splitTestEnabled('remove-mobile-filters-apply')) {
        this.setSearchFiltersHiddenKey('mobile-filter-panel', document.body.classList.contains('has-active-mobile-sidebar'));
      }
    }
  }

  setSearchFiltersHiddenKey(key, boolean) {
    const searchFiltersCookie = Cookies.get('ezSearchFiltersHidden');
    const searchFiltersHidden = searchFiltersCookie
      ? JSON.parse(searchFiltersCookie)
      : this.buildDefaultHiddenFiltersCookie();

    if (boolean && !searchFiltersHidden.includes(key)) {
      searchFiltersHidden.push(key);
    } else if (!boolean && searchFiltersHidden.includes(key)) {
      searchFiltersHidden.splice(searchFiltersHidden.indexOf(key), 1);
    }

    Cookies.set('ezSearchFiltersHidden', JSON.stringify(searchFiltersHidden));
  }

  buildDefaultHiddenFiltersCookie() {
    const cookie = [];

    const hiddenFilters = document.querySelectorAll('.ez-search-filters__sidebar-section.is-collapsed');

    hiddenFilters.forEach((filter) => {
      cookie.push(filter.dataset.collapseKey);
    });

    return cookie;
  }

  resetHiddenFilters = () => {
    Cookies.remove('ezSearchFiltersHidden');
  }

  hideSearchFilters = () => {
    // remove only, so applying filters on mobile doesn't send GA event.
    document.body.classList.remove('has-active-mobile-sidebar');
    document.body.classList.remove('has-active-sidebar');
  }

  toggleFilterSection(event) {
    const filterSection = event.srcElement.closest('.ez-search-filters__sidebar-section');
    const filterSectionBoolean = !filterSection.classList.contains('is-collapsed');

    filterSection.classList.toggle('is-collapsed');

    this.setSearchFiltersHiddenKey(filterSection.dataset.collapseKey, filterSectionBoolean);
  }

  sendShowHideEvent(windowWidth) {
    const hasActiveSidebar = document.body.classList.contains('has-active-sidebar');
    const hasActiveMobileSidebar = document.body.classList.contains('has-active-mobile-sidebar');
    let action = null;

    if (windowWidth > 750) {
      action = hasActiveSidebar ? 'hide' : 'unhide';
    } else if (windowWidth <= 750) {
      action = hasActiveMobileSidebar ? 'hide' : 'unhide';
    }

    triggerCustomEvent(events.search.hide_show_search_filters, {
      action,
      searchTerm: this.searchForm.dataset.searchTerm,
    });
  }

  get searchForm() {
    return document.querySelector("[data-controller~='search-form']");
  }

  get searchTerm() {
    if (this.searchForm) {
      return this.searchForm.getAttribute('data-search-term');
    }
    return null;
  }

  get curUserOrder() {
    const curUserOrder = sessionStorage.getItem('user_filter_order') || '[]';

    return JSON.parse(curUserOrder);
  }
}
